import { Navigate, Outlet } from 'react-router-dom'
import { useAuth } from '@/auth/AuthContext'
import { admits, type AccessRule } from '@/auth/access'

interface RequireAccessProps {
  /** Who may open the nested pages: the same rule the API holds their routes to. */
  rule: AccessRule
  /** Where to send a signed-in user it refuses. Defaults to `/`, which sends them
   * to their own home (avoiding loops between pages that refuse each other). */
  redirectTo?: string
}

/**
 * Route guard nested inside `ProtectedRoute` (so the user is already
 * authenticated here): renders the child routes only for someone `rule` lets in
 * and otherwise redirects — e.g. a client who navigates to a staff page is sent
 * back to their events, and staff whose role doesn't see furniture are sent home.
 *
 * CSE supporting a company works there as its organization admin, and leaves the
 * platform's own pages behind until it exits.
 */
export function RequireAccess({ rule, redirectTo = '/' }: RequireAccessProps) {
  const { user } = useAuth()

  if (!admits(user, rule)) {
    return <Navigate to={redirectTo} replace />
  }

  return <Outlet />
}
